import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Wrench, Plus, Users, Building2, MapPin, Zap, 
  ArrowRight, Trash2, RefreshCw, ChevronRight,
  BarChart3, Eye, ArrowLeft, Home
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DevPanel = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", email: "" });
  const [stats, setStats] = useState({ clients: 0, brands: 0, campaigns: 0, locations: 0 });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
      
      // Calcular stats
      let totalBrands = 0;
      let totalCampaigns = 0;
      let totalLocations = 0;
      
      for (const client of res.data) {
        try {
          const dashRes = await axios.get(`${API}/dashboard/${client.id}`);
          totalBrands += dashRes.data.brands?.length || 0;
          totalCampaigns += dashRes.data.recent_campaigns?.length || 0;
          totalLocations += dashRes.data.locations?.length || 0;
        } catch (e) {}
      }
      
      setStats({
        clients: res.data.length,
        brands: totalBrands,
        campaigns: totalCampaigns,
        locations: totalLocations
      });
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const createClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast.error("Preencha nome e email");
      return;
    }
    
    setCreating(true);
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      toast.success(`Cliente "${res.data.name}" criado!`);
      setNewClient({ name: "", email: "" });
      setShowNewClient(false);
      loadClients();
    } catch (error) {
      toast.error("Erro ao criar cliente");
    } finally {
      setCreating(false);
    }
  };

  const quickCreateTestClient = async () => {
    setCreating(true);
    try {
      const timestamp = Date.now();
      const res = await axios.post(`${API}/clients`, {
        name: `Cliente Teste ${timestamp}`,
        email: `teste${timestamp}@dev.local`
      });
      toast.success(`Cliente de teste criado!`);
      
      // Ir direto para o wizard com o novo cliente
      navigate(`/wizard?clientId=${res.data.id}`);
    } catch (error) {
      toast.error("Erro ao criar cliente");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/20 border-b border-yellow-600/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-black" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-yellow-400">Painel DEV</h1>
                <p className="text-sm text-gray-400">Ambiente de desenvolvimento</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-gray-400 hover:text-white"
              >
                <Home className="w-4 h-4 mr-1" />
                Início
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Login
              </Button>
              <Badge variant="outline" className="border-yellow-600 text-yellow-400">
                999.999 tokens
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.clients}</p>
                  <p className="text-sm text-gray-400">Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.brands}</p>
                  <p className="text-sm text-gray-400">Marcas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Zap className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.campaigns}</p>
                  <p className="text-sm text-gray-400">Campanhas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.locations}</p>
                  <p className="text-sm text-gray-400">Locais</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card 
            className="bg-gradient-to-br from-green-900/30 to-green-600/10 border-green-600/50 hover:border-green-500 cursor-pointer transition-all"
            onClick={quickCreateTestClient}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-green-400">Novo Cliente + Wizard</h3>
                  <p className="text-sm text-gray-400">Criar cliente e ir para wizard</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-blue-900/30 to-blue-600/10 border-blue-600/50 hover:border-blue-500 cursor-pointer transition-all"
            onClick={() => setShowNewClient(!showNewClient)}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-blue-400">Cadastrar Cliente</h3>
                  <p className="text-sm text-gray-400">Criar cliente personalizado</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-purple-900/30 to-purple-600/10 border-purple-600/50 hover:border-purple-500 cursor-pointer transition-all"
            onClick={loadClients}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-400">Atualizar Dados</h3>
                  <p className="text-sm text-gray-400">Recarregar lista de clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* New Client Form */}
        {showNewClient && (
          <Card className="bg-[#1a1a1a] border-blue-600/50 mb-8">
            <CardHeader>
              <CardTitle className="text-blue-400">Novo Cliente</CardTitle>
              <CardDescription>Preencha os dados do novo cliente</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Nome</Label>
                  <Input
                    placeholder="Nome do cliente"
                    value={newClient.name}
                    onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                    className="bg-[#252525] border-gray-700"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    className="bg-[#252525] border-gray-700"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={createClient}
                    disabled={creating}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {creating ? "Criando..." : "Criar Cliente"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowNewClient(false)}
                    className="border-gray-600"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Clients List */}
        <Card className="bg-[#1a1a1a] border-gray-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Clientes Cadastrados</CardTitle>
                <CardDescription>Selecione um cliente para gerenciar</CardDescription>
              </div>
              <Badge variant="outline" className="text-gray-400">
                {clients.length} clientes
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto"></div>
                <p className="text-gray-400 mt-2">Carregando...</p>
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Nenhum cliente cadastrado</p>
                <Button
                  onClick={quickCreateTestClient}
                  className="mt-4 bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Cliente
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="bg-[#252525] rounded-lg p-4 hover:bg-[#303030] transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-gray-400">{client.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/wizard?clientId=${client.id}`)}
                          className="border-green-600 text-green-400 hover:bg-green-600 hover:text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Nova Marca
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/dashboard/${client.id}`)}
                          className="border-blue-600 text-blue-400 hover:bg-blue-600 hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Dashboard
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            🛠️ Modo Desenvolvimento • Tokens ilimitados • Dados de teste
          </p>
        </div>
      </div>
    </div>
  );
};

export default DevPanel;
